import React, { useState, useEffect, useMemo } from "react";
import T from '../../../components/common/T';
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

// Contexts
import { useInternalPicking } from "../../../context/InternalPickingContext";
import { useInventory } from "../../../context/InventoryContext";
import { useProjects } from "../../../context/ProjectContext";
import { useCostSheets } from "../../../context/CostSheetContext";
import { useTasks } from "../../../context/TaskContext";
import { useConsumption } from "../../../context/ConsumptionContext"; 

// Components
import OdooFormLayout from "../../../components/OdooFormLayout"; 
import SmartButton from "../../../components/SmartButton";
import { TruckIcon, TrashIcon, PlusIcon } from "@heroicons/react/24/outline";

export default function InternalPickingForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = id === 'new';

  const { pickings = [], createPicking, updatePicking, updateStatus } = useInternalPicking();
  const { inventories = [], warehouses = [] } = useInventory(); 
  const { projects = [] } = useProjects();
  const { tasks = [] } = useTasks();
  const { getProjectCostSheets } = useCostSheets(); 
  const { issueMaterial } = useConsumption(); 

  const [activeTab, setActiveTab] = useState("lines");
  const [loading, setLoading] = useState(false);
  
  const [formData, setFormData] = useState({
    id: "",
    employee: "Mitchell Admin",
    department: "Management",
    company: "My Company",
    projectId: "",
    projectName: "",
    taskId: "", 
    costSheetId: "",
    analyticAccount: "",
    requisitionDate: new Date().toISOString().split('T')[0],
    receivedDate: "",
    deadline: "",
    responsible: "Eli Lambert",
    status: "NEW",
    pickingTransferRef: "",
    sourceWarehouseId: "", 
    lines: [] 
  });

  const STAGES = ["NEW", "WAITING DEPARTMENT APPROVAL", "WAITING IR APPROVAL", "APPROVED", "PICKING CREATED", "RECEIVED"];

  const formatDateForInput = (dateString) => {
      if (!dateString) return "";
      return new Date(dateString).toISOString().split('T')[0];
  };

  useEffect(() => {
    if (isNew) return;
    setLoading(true);
    
    const existingPicking = pickings.find(p => String(p.id) === String(id));
    
    if (existingPicking) {
        setFormData(prev => ({
            ...prev,
            ...existingPicking,
            requisitionDate: formatDateForInput(existingPicking.requisitionDate),
            receivedDate: formatDateForInput(existingPicking.receivedDate),
            deadline: formatDateForInput(existingPicking.deadline),
            lines: existingPicking.lines || [] 
        }));
    } else {
        toast.error("Failed to load details or picking not found.");
    }
    setLoading(false);
  }, [id, isNew, pickings]);

  const availableCostSheets = useMemo(() => formData.projectId ? getProjectCostSheets(formData.projectId) : [], [formData.projectId, getProjectCostSheets]);
  
  const currentWarehouseBins = useMemo(() => {
      const wh = warehouses.find(w => String(w.id) === String(formData.sourceWarehouseId));
      return wh ? wh.bins : ["General"]; 
  }, [warehouses, formData.sourceWarehouseId]);

  const handleChange = (e) => setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));

  const handleProjectChange = (e) => {
    const pid = e.target.value;
    const proj = projects.find(p => String(p.id) === String(pid));
    setFormData(prev => ({
      ...prev,
      projectId: pid,
      projectName: proj?.name || "",
      analyticAccount: proj?.name || "", 
      costSheetId: "", 
      taskId: ""
    }));
  };

  const updateLine = (index, field, value) => {
      const newLines = [...formData.lines];
      newLines[index][field] = value;
      
      if (field === 'materialId') {
          const item = inventories.find(i => String(i.id) === String(value));
          if (item) {
              newLines[index].description = item.itemName; 
              newLines[index].unit = item.unit || "Units";
              
              if (item.locations && item.locations.length > 0) {
                  const bestLocation = [...item.locations].sort((a, b) => b.qty - a.qty)[0];
                  newLines[index].sourceBin = bestLocation.bin;
                  if(bestLocation.warehouseId !== formData.sourceWarehouseId) {
                      setFormData(prev => ({ ...prev, sourceWarehouseId: bestLocation.warehouseId }));
                      toast.info(`Switched Source Warehouse to match stock location: ${bestLocation.bin}`);
                  }
              }
          }
      }
      setFormData(prev => ({ ...prev, lines: newLines }));
  };

  const addLine = () => {
      const defaultBin = currentWarehouseBins.length > 0 ? currentWarehouseBins[0] : "";
      setFormData(prev => ({ ...prev, lines: [...prev.lines, { id: Date.now(), materialId: "", description: "", quantity: 1, unit: "Units", sourceBin: defaultBin }] }));
  };
  
  const removeLine = (index) => setFormData(prev => ({ ...prev, lines: prev.lines.filter((_, i) => i !== index) }));

  // --- ACTION HANDLERS ---
  const handleConfirm = () => {
      console.log("Confirming Payload:", formData);
      if(!formData.projectId) return toast.error("Select Construction Project");
      try {
          if(isNew) {
              const newId = `REQ-${Date.now().toString().slice(-4)}`;
              const newPayload = { ...formData, id: newId, status: "WAITING DEPARTMENT APPROVAL" };
              if (createPicking) createPicking(newPayload);
              navigate(`/admin/material/internal-picking/${newId}`, { replace: true });
              toast.success("Picking Created");
          } else {
              if (updatePicking) updatePicking(id, { ...formData, status: "WAITING DEPARTMENT APPROVAL" });
              setFormData(prev => ({ ...prev, status: "WAITING DEPARTMENT APPROVAL" }));
              toast.success("Confirmed");
          }
      } catch (err) {
          console.error(err);
          toast.error("Error saving picking");
      }
  };

  const handleApprove = () => {
      const next = formData.status === "WAITING DEPARTMENT APPROVAL" ? "WAITING IR APPROVAL" : "APPROVED";
      try {
          if (updateStatus) updateStatus(id, next);
          else if (updatePicking) updatePicking(id, { ...formData, status: next });
          
          setFormData(prev => ({ ...prev, status: next }));
          toast.success("Approved");
      } catch (err) {
          toast.error("Error updating status");
      }
  };

  const handleCreatePicking = () => {
      const ref = `WH/INT/${Date.now().toString().slice(-5)}`;
      try {
          if (updatePicking) updatePicking(id, { ...formData, status: "PICKING CREATED", pickingTransferRef: ref });
          setFormData(prev => ({ ...prev, status: "PICKING CREATED", pickingTransferRef: ref }));
          toast.success("Picking Created");
      } catch (err) {
          toast.error("Error creating picking");
      }
  };

  const handleValidate = () => {
      console.log("Validation Triggered! Current State:", formData);
      
      if(!formData.sourceWarehouseId) return toast.error("Please select a Source Warehouse.");
      if(!formData.lines || formData.lines.length === 0) return toast.error("Add at least one item to validate.");

      try {
          for (const line of formData.lines) {
              if (!line.materialId) throw new Error("A line is missing a product selection.");
              
              const item = inventories.find(i => String(i.id) === String(line.materialId));
              const loc = item?.locations?.find(l => 
                  String(l.warehouseId) === String(formData.sourceWarehouseId) && 
                  String(l.bin) === String(line.sourceBin)
              );

              if (!loc || Number(loc.qty) < Number(line.quantity)) {
                  throw new Error(`Insufficient stock for ${item?.itemName || 'Product'} in ${line.sourceBin}.`);
              }
          }

          console.log("Stock check passed. Issuing material to project...");
          
          if (issueMaterial) {
              formData.lines.forEach(line => {
                  issueMaterial({
                      projectId: formData.projectId,
                      materialId: line.materialId,
                      quantity: Number(line.quantity),
                      warehouseId: formData.sourceWarehouseId,
                      bin: line.sourceBin,
                      costSheetId: formData.costSheetId
                  });
              });
          }

          const receiveDate = new Date().toISOString().split('T')[0];
          if (updateStatus) updateStatus(id, "RECEIVED");
          else if (updatePicking) updatePicking(id, { ...formData, status: "RECEIVED", receivedDate: receiveDate });

          setFormData(prev => ({ ...prev, status: "RECEIVED", receivedDate: receiveDate }));
          toast.success("Validated! Stock Moved & Cost Booked to Project.");

      } catch (error) {
          console.error("Validation Error:", error);
          toast.error(error.message || "Validation failed"); 
      }
  };

  if (loading) return <div className="pt-24 px-8 text-gray-500"><T>Loading Requisition...</T></div>;

  return (
    <>
      <div className="pt-24"> 
        <OdooFormLayout
          title={formData.pickingTransferRef ? `${formData.id} / ${formData.pickingTransferRef}` : formData.id || "New"}
          subTitle="Internal Picking"
          status={formData.status}
          stages={STAGES}
          actions={
            <>
              {/* ADDED type="button" TO EVERY SINGLE ACTION BUTTON TO PREVENT FORM REFRESHES */}
              {formData.status === "NEW" && <button type="button" onClick={handleConfirm} className="bg-purple-700 text-white px-3 py-1.5 text-xs font-bold uppercase rounded hover:bg-purple-800 transition"><T>Confirm</T></button>}
              {(formData.status.includes("WAITING")) && <button type="button" onClick={handleApprove} className="bg-purple-700 text-white px-3 py-1.5 text-xs font-bold uppercase rounded hover:bg-purple-800 transition"><T>Approve</T></button>}
              {formData.status === "APPROVED" && <button type="button" onClick={handleCreatePicking} className="bg-purple-700 text-white px-3 py-1.5 text-xs font-bold uppercase rounded hover:bg-purple-800 transition"><T>Create Picking</T></button>}
              {formData.status === "PICKING CREATED" && <button type="button" onClick={handleValidate} className="bg-purple-700 text-white px-3 py-1.5 text-xs font-bold uppercase rounded hover:bg-purple-800 transition"><T>Validate</T></button>}
              <button type="button" onClick={() => navigate('/admin/material/internal-picking')} className="ml-2 border border-gray-300 bg-white text-gray-700 px-3 py-1.5 text-xs font-bold uppercase rounded"><T>Back</T></button>
            </>
          }
          smartButtons={formData.pickingTransferRef && <SmartButton label="Transfers" value="1" icon={TruckIcon} onClick={() => setActiveTab('picking')} />}
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-4 mb-8">
              <div className="space-y-3">
                  <div className="flex items-center"><label className="w-40 text-sm font-bold text-gray-700"><T>Employee</T></label><input name="employee" value={formData.employee} onChange={handleChange} className="flex-1 bg-transparent border-b border-gray-300 outline-none text-sm py-1" /></div>
                  <div className="flex items-center"><label className="w-40 text-sm font-bold text-gray-700"><T>Construction Project</T></label><select name="projectId" value={formData.projectId} onChange={handleProjectChange} className="flex-1 bg-transparent border-b border-gray-300 outline-none text-sm py-1"><option value=""><T>Select Project...</T></option>{projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}</select></div>
                  <div className="flex items-center"><label className="w-40 text-sm font-bold text-gray-700"><T>Job Cost Center</T></label><select name="costSheetId" value={formData.costSheetId} onChange={handleChange} className="flex-1 bg-transparent border-b border-gray-300 outline-none text-sm py-1"><option value=""><T>Select Cost Sheet...</T></option>{availableCostSheets.map(cs => <option key={cs.id} value={cs.id}>{cs.name}</option>)}</select></div>
              </div>
              <div className="space-y-3">
                   <div className="flex items-center"><label className="w-40 text-sm font-bold text-gray-700"><T>Requisition Date</T></label><input type="date" name="requisitionDate" value={formData.requisitionDate} onChange={handleChange} className="flex-1 bg-transparent border-b border-gray-300 outline-none text-sm py-1" /></div>
                   <div className="flex items-center"><label className="w-40 text-sm font-bold text-gray-700"><T>Analytic Account</T></label><input value={formData.analyticAccount} readOnly className="flex-1 bg-transparent border-b border-gray-300 outline-none text-sm py-1 text-purple-600 font-medium" /></div>
                   <div className="flex items-center"><label className="w-40 text-sm font-bold text-gray-700"><T>Responsible</T></label><input name="responsible" value={formData.responsible} onChange={handleChange} className="flex-1 bg-transparent border-b border-gray-300 outline-none text-sm py-1" /></div>
              </div>
          </div>

          <div className="mt-8">
              <div className="flex border-b border-gray-200 mb-4 gap-6">
                  <button type="button" onClick={() => setActiveTab("lines")} className={`pb-2 px-1 text-sm font-bold uppercase border-b-2 transition ${activeTab === "lines" ? "text-purple-700 border-purple-700" : "text-gray-500 border-transparent hover:text-gray-700"}`}><T>Internal Requisitions Lines</T></button>
                  <button type="button" onClick={() => setActiveTab("picking")} className={`pb-2 px-1 text-sm font-bold uppercase border-b-2 transition ${activeTab === "picking" ? "text-purple-700 border-purple-700" : "text-gray-500 border-transparent hover:text-gray-700"}`}><T>Picking Details</T></button>
              </div>
              
              {activeTab === "lines" && (
                <>
                  <table className="w-full text-left text-sm text-gray-700">
                      <thead className="bg-gray-50 uppercase text-xs font-bold text-gray-500">
                          <tr>
                              <th className="px-3 py-2"><T>Product</T></th>
                              <th className="px-3 py-2"><T>Description</T></th>
                              <th className="px-3 py-2 w-1/6"><T>Source Bin</T></th>
                              <th className="px-3 py-2 text-right"><T>Quantity</T></th>
                              <th className="px-3 py-2 w-10"></th>
                          </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                          {formData.lines.map((line, idx) => (
                              <tr key={idx} className="hover:bg-gray-50">
                                  <td className="px-3 py-2">
                                      <select 
                                        value={line.materialId} 
                                        onChange={(e) => updateLine(idx, 'materialId', e.target.value)} 
                                        className="w-full bg-transparent outline-none focus:text-purple-700" 
                                        disabled={formData.status === "RECEIVED"}
                                      >
                                        <option value=""><T>Select Product...</T></option>
                                        {inventories.map(i => {
                                            let stockDisplay = "";
                                            if (i.locations && i.locations.length > 0) stockDisplay = i.locations.map(l => `${l.bin}: ${l.qty}`).join(", ");
                                            return <option key={i.id} value={i.id}>{i.itemName} {stockDisplay ? `(${stockDisplay})` : ''}</option>;
                                        })}
                                      </select>
                                  </td>
                                  <td className="px-3 py-2"><input value={line.description} onChange={(e) => updateLine(idx, 'description', e.target.value)} className="w-full bg-transparent outline-none" disabled={formData.status === "RECEIVED"} /></td>
                                  <td className="px-3 py-2">
                                      <select value={line.sourceBin} onChange={(e) => updateLine(idx, 'sourceBin', e.target.value)} className="w-full bg-transparent outline-none text-gray-600 focus:text-purple-700" disabled={formData.status === "RECEIVED"}>
                                          {currentWarehouseBins.map(bin => <option key={bin} value={bin}>{bin}</option>)}
                                      </select>
                                  </td>
                                  <td className="px-3 py-2 text-right"><input type="number" value={line.quantity} onChange={(e) => updateLine(idx, 'quantity', e.target.value)} className="w-20 text-right bg-transparent outline-none border-b border-transparent focus:border-purple-600" disabled={formData.status === "RECEIVED"} /></td>
                                  <td className="px-3 py-2 text-center">{formData.status !== "RECEIVED" && (<button type="button" onClick={() => removeLine(idx)} className="text-gray-300 hover:text-red-500"><TrashIcon className="w-4 h-4" /></button>)}</td>
                              </tr>
                          ))}
                      </tbody>
                  </table>
                  {formData.status !== "RECEIVED" && <button type="button" onClick={addLine} className="mt-3 text-purple-700 text-xs font-bold uppercase hover:underline flex items-center gap-1"><PlusIcon className="w-3 h-3"/><T> Add a line</T></button>}
                </>
              )}

              {activeTab === "picking" && (
                   <div className="grid grid-cols-2 gap-12 text-sm text-gray-800 p-4 border border-gray-100 rounded bg-gray-50">
                        <div className="space-y-4">
                            <h4 className="font-bold text-gray-500 uppercase text-xs border-b pb-1"><T>Operations</T></h4>
                            <div className="flex justify-between"><span className="text-gray-500"><T>Operation Type</T></span><span className="font-medium text-purple-700"><T>Internal Transfers</T></span></div>
                            <div className="flex justify-between items-center">
                                <span className="text-gray-500"><T>Source Location</T></span>
                                <select 
                                    className="font-medium bg-transparent outline-none text-right focus:text-purple-700"
                                    value={formData.sourceWarehouseId}
                                    onChange={(e) => setFormData(p => ({...p, sourceWarehouseId: e.target.value}))}
                                    disabled={formData.status === "RECEIVED"}
                                >
                                    <option value="">Select Warehouse...</option>
                                    {warehouses.map(w => <option key={w.id} value={w.id}>{w.name}</option>)}
                                </select>
                            </div>
                            <div className="flex justify-between"><span className="text-gray-500"><T>Destination Location</T></span><span className="font-medium">{formData.projectName || "Project Site"}</span></div>
                        </div>
                   </div>
              )}
          </div>
        </OdooFormLayout>
      </div>
    </>
  );
}